import { useState, useEffect } from 'react';
import { adminAPI } from '../../lib/api';
import { ClipboardList, Users, CheckCircle, AlertCircle, TrendingUp, Clock } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Link } from 'react-router-dom';

const STATUS_COLORS = {
  'Yeni': 'bg-blue-100 text-blue-800',
  'Arandi': 'bg-yellow-100 text-yellow-800',
  'Yonlendirildi': 'bg-green-100 text-green-800',
  'Sonuclandirildi': 'bg-indigo-100 text-indigo-800',
  'Kaybedildi': 'bg-red-100 text-red-800',
};

export default function DashboardPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    adminAPI.getDashboard().then(r => setData(r.data)).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[hsl(221,83%,22%)]"></div></div>;
  if (!data) return null;

  const kpis = [
    { label: 'Toplam Lead', value: data.total_leads, icon: ClipboardList, color: 'text-blue-600' },
    { label: 'Yeni (Bekleyen)', value: data.new_leads, icon: AlertCircle, color: 'text-amber-600' },
    { label: 'Yonlendirilen', value: data.assigned_leads, icon: TrendingUp, color: 'text-green-600' },
    { label: 'Bugun', value: data.today_leads, icon: Clock, color: 'text-indigo-600' },
  ];

  return (
    <div data-testid="admin-dashboard">
      <h1 className="text-2xl font-semibold mb-6">Dashboard</h1>

      {/* KPI Cards */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {kpis.map((kpi, i) => (
          <Card key={i} data-testid={`kpi-${i}`}>
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">{kpi.label}</p>
                  <p className="text-3xl font-semibold mt-1 tabular-nums">{kpi.value}</p>
                </div>
                <kpi.icon className={`h-8 w-8 ${kpi.color} opacity-50`} />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Recent Leads */}
      <Card className="mb-6">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-lg">Son Leadler</CardTitle>
          <Link to="/admin/leads" className="text-sm text-[hsl(221,83%,22%)] hover:underline">Tumunu Gor</Link>
        </CardHeader>
        <CardContent>
          {data.recent_leads.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4 text-center">Henuz lead yok</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2 px-3 font-medium text-muted-foreground">Tarih</th>
                    <th className="text-left py-2 px-3 font-medium text-muted-foreground">Ad Soyad</th>
                    <th className="text-left py-2 px-3 font-medium text-muted-foreground">Sehir</th>
                    <th className="text-left py-2 px-3 font-medium text-muted-foreground">Hizmet</th>
                    <th className="text-left py-2 px-3 font-medium text-muted-foreground">Durum</th>
                  </tr>
                </thead>
                <tbody>
                  {data.recent_leads.map(lead => (
                    <tr key={lead.id} className="border-b last:border-0 hover:bg-slate-50">
                      <td className="py-2.5 px-3 text-muted-foreground tabular-nums">{new Date(lead.created_at).toLocaleDateString('tr-TR')}</td>
                      <td className="py-2.5 px-3 font-medium">
                        <Link to={`/admin/leads/${lead.id}`} className="hover:text-[hsl(221,83%,22%)] hover:underline">{lead.ad_soyad}</Link>
                      </td>
                      <td className="py-2.5 px-3">{lead.sehir}</td>
                      <td className="py-2.5 px-3">{lead.hizmet}</td>
                      <td className="py-2.5 px-3">
                        <Badge variant="secondary" className={STATUS_COLORS[lead.status] || ''}>{lead.status}</Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Recent Audit Logs */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-lg">Son Islemler</CardTitle>
          <Link to="/admin/audit-logs" className="text-sm text-[hsl(221,83%,22%)] hover:underline">Tumunu Gor</Link>
        </CardHeader>
        <CardContent>
          {data.recent_logs.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4 text-center">Henuz islem kaydi yok</p>
          ) : (
            <div className="space-y-3">
              {data.recent_logs.map(log => (
                <div key={log.id} className="flex items-start gap-3 text-sm border-b pb-3 last:border-0">
                  <div className="w-2 h-2 rounded-full bg-[hsl(221,83%,22%)] mt-1.5 flex-shrink-0" />
                  <div>
                    <p><span className="font-medium">{log.user_email}</span> - {log.action} ({log.entity})</p>
                    {log.details && <p className="text-muted-foreground text-xs mt-0.5">{log.details}</p>}
                    <p className="text-xs text-muted-foreground mt-0.5 tabular-nums">{new Date(log.created_at).toLocaleString('tr-TR')}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
